import moment from 'moment'
import { NextPage } from 'next'
import Image from 'next/image'
import Link from 'next/link'
import { TailSpin } from 'react-loading-icons'
import { WriterSection } from 'src/components/Author/WriterSection'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import { MaterialSection } from 'src/components/Material/MaterialSection'
import { useContentBySlugQuery } from 'src/graphql/generated/hooks'
import { useContentWriterInformation } from 'src/hooks/useContentWriterInformation'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'

type Props = {
  slug: string
  contentSnippet?: string
}

export const ContentPageView: NextPage<Props> = ({ slug, contentSnippet = '' }) => {
  const { data, loading } = useContentBySlugQuery({
    fetchPolicy: 'cache-and-network',
    variables: { slug },
  })
  const title = data?.contentBySlug?.content?.attributes?.title || ''
  const writer = data?.contentBySlug?.content?.attributes?.writer
  const publishedAt = moment(data?.contentBySlug?.content?.attributes?.publishedTime).format('MMMM DD, YYYY')
  const defaultImageUrl =
    data?.contentBySlug?.content?.attributes?.defaultImage?.data &&
    data?.contentBySlug?.content?.attributes?.defaultImage?.data.length > 0
      ? data?.contentBySlug?.content?.attributes?.defaultImage?.data[0].attributes?.url
      : null
  const imageUrl = useDefaultLogo(defaultImageUrl)
  const showImage = !!defaultImageUrl

  const isContentPremium = !!data?.contentBySlug?.content?.attributes?.isPremium
  const fullContent = data?.contentBySlug || null
  const downloadMaterial = fullContent?.content?.attributes?.downloadMaterial
  const fullContentSnippet = fullContent?.content?.attributes?.snippet || ''
  const isInvited = fullContent?.isInvited || false

  const writerInfo = useContentWriterInformation(writer)

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center my-[40px]">
        <TailSpin stroke="#011D58" height="2em" /> <div className="pl-3"> Loading...</div>
      </div>
    )
  }

  return (
    <div className="w-full items-center justify-between">
      <div className="!max-w-[1100px] flex flex-col w-full m-auto bg-white">
        <div className="flex flex-col md:pl-[4.0625rem] md:pr-[4.0625rem] pl-[20px] pr-[20px]">
          <h1 className="pt-[0.1rem] pb-[0.125rem] font-bold font-secondary text-darkgray text-[1.875rem] mt-[2rem]">
            {title}
          </h1>
          <div className="flex flex-row mb-[2.2rem] mt-[1rem] items-center flex-wrap">
            <span className="resource-desc mr-[0.425rem]" style={{ fontSize: '18px' }}>
              Published on {publishedAt}
            </span>
            {writerInfo?.name && (
              <>
                <span className="resource-desc mr-[0.425rem]" style={{ fontSize: '18px' }}>
                  &nbsp;by
                </span>
                {writerInfo?.url ? (
                  <Link href={writerInfo?.url}>
                    <span className="resource-normal-link underline cursor-pointer" style={{ fontSize: '18px' }}>
                      {writerInfo?.name}
                    </span>
                  </Link>
                ) : (
                  <div>
                    <span className="resource-normal-link underline cursor-pointer" style={{ fontSize: '18px' }}>
                      {writerInfo?.name}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="divide-x h-[1px] bg-mist mb-[1.6rem]"></div>
          {downloadMaterial && (
            <div className="flex flex-col md:flex-row mb-[2rem] py-[1rem]">
              <div className="md:w-1/2 md:overflow-hidden">
                <HtmlContentViewer
                  className="blog-content html-show font-secondary font-semibold text-darkgray text-[22px] leading-[32px]"
                  html={contentSnippet}
                />
              </div>
              <div className="md:w-1/2 md:ml-[40px] mt-10 md:mt-0">
                <MaterialSection
                  isInvited={isInvited}
                  contentId={data?.contentBySlug?.content?.id}
                  downloadMaterial={downloadMaterial}
                  isContentPremium={isContentPremium}
                  onDisabledDownloadClick={() => {}}
                />
              </div>
            </div>
          )}
          <HtmlContentViewer
            className="blog-content html-show mb-[3rem] w-full overflow-auto"
            html={fullContentSnippet}
            image={
              imageUrl && showImage ? (
                <Image
                  className="w-full bg-gray object-contain	"
                  alt="logo"
                  src={imageUrl}
                  width={1000}
                  height={1000}
                  priority
                />
              ) : null
            }
          />
          {writer && <WriterSection writer={writer} />}
        </div>
      </div>
    </div>
  )
}
